import * as THREE from 'three';
import {
  FeatureResultMesh,
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
} from '../../mesh/feature';
import type { SelectionTarget } from '../loaders/types';
import { ImportedModelObjectMetadataService } from '../metadata';
import {
  resolveImportedModelTopologyRepository,
} from '../topology/ImportedModelTopologyRepository';
import { isImportedModelTopologyHost } from '../topology/imported-model-topology-runtime';

/**
 * 装配拓扑选择引用协议版本。
 */
export const ASSEMBLY_TOPOLOGY_SELECTION_REF_VERSION = 1;

/**
 * 解析当前拓扑代理所属的 pick host。
 *
 * @param object 当前选择对象。
 * @returns imported pick host；不存在时返回 `null`。
 */
function resolveImportedPickHost(object: THREE.Object3D): FeatureResultMesh | null {
  if (object instanceof FeatureResultMesh && isImportedModelTopologyHost(object)) {
    return object;
  }

  if (isFeatureFace(object) || isFeatureLine(object) || isFeaturePoint(object)) {
    const owner = object.getOwningResultMesh();
    return owner && isImportedModelTopologyHost(owner)
      ? owner as FeatureResultMesh
      : null;
  }

  let current: THREE.Object3D | null = object.parent;
  while (current) {
    if (current instanceof FeatureResultMesh && isImportedModelTopologyHost(current)) {
      return current;
    }
    current = current.parent;
  }

  return null;
}

/**
 * 从场景层级读取导入选择目标。
 *
 * @param object 当前选择对象。
 * @returns 选择目标；无法解析时返回 `null`。
 */
function resolveImportedSelectionTarget(object: THREE.Object3D): SelectionTarget | null {
  const directTarget = ImportedModelObjectMetadataService.resolveSelectionTargetFromSelfOrAncestors(object);
  if (directTarget && typeof directTarget.assemblyNodeId === 'string') {
    return {
      assemblyNodeId: directTarget.assemblyNodeId,
      partUuid: typeof directTarget.partUuid === 'string' ? directTarget.partUuid : undefined,
      primitiveId: typeof directTarget.primitiveId === 'string' ? directTarget.primitiveId : undefined,
      stepFaceId: typeof directTarget.stepFaceId === 'string' ? directTarget.stepFaceId : undefined,
    };
  }

  const assemblyNodeId = ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(object);
  if (typeof assemblyNodeId === 'string') {
    return {
      assemblyNodeId,
      partUuid: ImportedModelObjectMetadataService.resolvePartFileUuidFromSelfOrAncestors(object),
    };
  }

  return null;
}

/**
 * 读取 imported body 绑定 ID。
 *
 * @param host imported pick host。
 * @param topologyPick 当前拓扑选择快照。
 * @returns body 绑定 ID。
 */
function resolveBodyBindingId(
  host: FeatureResultMesh | null,
  topologyPick: ResolvedTopologyPick,
): string {
  const repository = resolveImportedModelTopologyRepository(host);
  const bodyRecord = host ? repository?.resolveBodyRecordFromPickHost(host) ?? null : null;
  return bodyRecord?.bodyBindingId
    ? bodyRecord.bodyBindingId
    : topologyPick.bodyObjectId;
}

/**
 * 将 imported Feature 选择转换为装配实例级拓扑引用。
 *
 * 设计说明：
 * 1. Mate 命令需要知道“哪个装配实例里的哪个 BRep 子实体”，不能只保存 FeatureLine/FeatureFace 的 uuid；
 * 2. 该函数沿场景父链读取 `Object3D.metadata.importedModel.selectionTarget`，同时从 pick host 读取 body 绑定；
 * 3. 函数不持有 Object3D 引用，返回值可直接进入命令、历史记录或后续 MateEntityRef。
 *
 * @param object 当前命中的 Feature 代理对象。
 * @param topologyPick 选择层已经解析出的拓扑快照。
 * @returns 装配拓扑选择引用；非 imported 装配对象返回 `null`。
 */
export function resolveImportedAssemblyTopologySelectionRef(
  object: THREE.Object3D | null | undefined,
  topologyPick: ResolvedTopologyPick | null | undefined,
): AssemblyTopologySelectionRef | null {
  if (!object || !topologyPick) {
    return null;
  }

  const pickHost = resolveImportedPickHost(object);
  const bodyBindingId = resolveBodyBindingId(pickHost, topologyPick);
  const bodySelectionTarget = ImportedModelObjectMetadataService
    .resolveBodyBindingSelectionTargetFromSelfOrAncestors(object, bodyBindingId);
  const selectionTarget = bodySelectionTarget?.assemblyNodeId
    ? {
        assemblyNodeId: bodySelectionTarget.assemblyNodeId,
        partUuid: typeof bodySelectionTarget.partUuid === 'string'
          ? bodySelectionTarget.partUuid
          : undefined,
        primitiveId: typeof bodySelectionTarget.primitiveId === 'string'
          ? bodySelectionTarget.primitiveId
          : undefined,
        stepFaceId: typeof bodySelectionTarget.stepFaceId === 'string'
          ? bodySelectionTarget.stepFaceId
          : undefined,
      }
    : resolveImportedSelectionTarget(object);
  if (!selectionTarget) {
    return null;
  }

  return {
    schemaVersion: ASSEMBLY_TOPOLOGY_SELECTION_REF_VERSION,
    assemblyNodeId: selectionTarget.assemblyNodeId,
    partUuid: selectionTarget.partUuid,
    bodyBindingId,
    bodyObjectId: topologyPick.bodyObjectId,
    entityKind: topologyPick.kind,
    topologyIndex: topologyPick.topologyIndex,
    subEntityId: topologyPick.subEntityId,
    persistentRef: topologyPick.persistentRef,
    shapeHash: topologyPick.runtimeTopologyHint.shapeHash,
    adjacentFaceRefs: topologyPick.adjacentFaceRefs,
  };
}
